import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Microphone, Lifebuoy, X, ArrowLeft } from "@phosphor-icons/react";
import { fmtMoney } from "@/lib/format";
import { OrderHeader } from "@/components/customer_order/OrderHeader";
import { MenuBrowser } from "@/components/customer_order/MenuBrowser";
import { CartLines, CartTotals } from "@/components/customer_order/CartLines";
import { ModifierModal, openModifiers, flattenModifiers } from "@/components/customer_order/ModifierModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cache = {};

// AI Cashier → Browse Menu: the SAME menu, modifier screen and cart as the No AI kiosk (shared components),
// working on the AI session's one authoritative cart. Touch browsing never calls the AI.
export function AiBrowseMenu({ boot, session, qr, activeCat, setActiveCat, onAction, onClose, onCheckout, help, notice }) {
  const mode = qr ? "qr" : "kiosk";
  const key = `${boot.venue_id}:${mode}`;
  const [menu, setMenu] = useState(cache[key] || null);
  const [modOpen, setModOpen] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [err, setErr] = useState("");
  const isKiosk = !qr;
  const touch = !!session.flow?.touch_only;
  useEffect(() => { // live menu, prices and stock on every open (cached copy shows instantly)
    axios.get(`${API}/public/menu/${boot.venue_id}`, { params: { mode } })
      .then((r) => { cache[key] = r.data; setMenu(r.data); if (!activeCat && r.data.categories[0]) setActiveCat(r.data.categories[0].id); })
      .catch(() => setErr("Could not load the menu — please try again."));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const cat = menu?.categories?.some((c) => c.id === activeCat) ? activeCat : menu?.categories?.[0]?.id;
  const items = useMemo(() => (menu?.items || []).filter((i) => !cat || i.category_id === cat), [menu, cat]);
  const gates = useMemo(() => Object.fromEntries(boot.menu.flatMap((m) => m.groups).map((g) => [g.id, g.combo_gate])), [boot.menu]);
  const gate = (g, picked) => !gates[g.id] || gates[g.id].some((id) => picked.includes(id)); // combo choices only with the combo

  const add = (it, optionIds = []) => onAction([{ type: "add_item", menu_item_id: it.id, qty: 1, option_ids: optionIds, heard: it.name }]);
  const addItem = (it) => {
    if (it.available === false || it.sold_out) return;
    if (it.modifier_group_ids?.length) setModOpen(openModifiers(it, menu.modifier_groups, gate));
    else add(it);
  };
  const confirmMods = () => {
    const { error, flat } = flattenModifiers(modOpen);
    if (error) { toast.error(error); return; } // same as the No AI kiosk
    add(modOpen.item, flat.flatMap((m) => Array(m.qty).fill(m.modifier_option_id)));
    setModOpen(null);
  };
  const lines = session.cart.map((ln, i) => ({ key: i + 1, name: ln.name, qty: ln.qty, total: ln.line_total,
    modifiers: Object.values(ln.modifiers.reduce((a, m) => { const k = m.modifier_option_id || m.name; a[k] = { name: m.name, qty: (a[k]?.qty || 0) + 1 }; return a; }, {})) }));
  const qtyOf = (n) => session.cart[n - 1]?.qty || 0;
  const cart = (
    <AiCart lines={lines} session={session} onCheckout={onCheckout} onClose={onClose}
      onInc={(n) => onAction([{ type: "update_line", line: n, qty: qtyOf(n) + 1 }])}
      onDec={(n) => qtyOf(n) > 1 && onAction([{ type: "update_line", line: n, qty: qtyOf(n) - 1 }])}
      onRemove={(n) => onAction([{ type: "update_line", line: n, qty: 0 }])} />
  );
  const msg = err || notice;
  return (
    <div className="fixed inset-0 z-40 overflow-y-auto bg-[var(--sand-50)] select-none" data-testid="ai-browse-menu" style={{ userSelect: "none" }}>
      <OrderHeader isKiosk={isKiosk} mode={mode} venueName={menu?.venue?.name || boot.venue}>
        <div className="flex items-center gap-2">
          <button data-testid="ai-browse-help" onClick={help.request}
            className={`px-4 ${isKiosk ? "h-12 text-base" : "h-10 text-sm"} rounded-full border border-[rgba(28,25,23,0.12)] bg-white text-[var(--ink-900)] font-medium inline-flex items-center gap-2 active:scale-95 transition-transform`}>
            <Lifebuoy size={18} /> <span className={isKiosk ? "" : "sr-only"}>{["open", "acknowledged", "sending"].includes(help.status) ? "Help requested" : "Get help"}</span>
          </button>
          {isKiosk && <BackToAi onClose={onClose} touch={touch} />}
        </div>
      </OrderHeader>
      {msg && <div className="max-w-[1300px] mx-auto px-4 sm:px-8 pt-4"><div className="px-4 py-2 rounded-md bg-amber-50 border border-amber-300 text-amber-900 text-sm" data-testid="ai-browse-notice">{msg}</div></div>}
      {help.message && <div className="max-w-[1300px] mx-auto px-4 sm:px-8 pt-4"><div className="px-4 py-2 rounded-md bg-[#fff2cc] text-[#5c4300] text-sm" data-testid="ai-browse-help-status">{help.message}</div></div>}
      <div className={`max-w-[1300px] mx-auto ${isKiosk ? "p-8" : "p-4 pb-24"} ${isKiosk ? "grid grid-cols-12 gap-8" : ""}`}>
        <div className={isKiosk ? "col-span-8" : ""}>
          {!menu ? <div className="text-center py-12 text-[var(--ink-500)] text-sm" data-testid="ai-browse-loading">Loading menu…</div>
            : <MenuBrowser cats={menu.categories} activeCat={cat} setActiveCat={setActiveCat} items={items} isKiosk={isKiosk} onAdd={addItem} />}
        </div>
        {isKiosk && (
          <aside className="col-span-4 sticky top-32 self-start bg-white border border-[rgba(28,25,23,0.08)] rounded-xl p-5 max-h-[calc(100vh-9rem)] overflow-y-auto" data-testid="kiosk-cart">{cart}</aside>
        )}
      </div>
      {!isKiosk && !cartOpen && (
        <div className="fixed bottom-4 left-4 right-4 z-40 flex gap-2">
          <BackToAi onClose={onClose} compact touch={touch} />
          {session.cart.length > 0 && (
            <button data-testid="floating-cart" onClick={() => setCartOpen(true)} className="flex-1 min-w-0 overflow-hidden h-14 rounded-full bg-[var(--ink-900)] text-[var(--sand-50)] flex items-center justify-between px-6 shadow-lg active:scale-[0.99]">
              <span className="text-sm font-medium truncate whitespace-nowrap">View Cart · {session.cart.length} items</span>
              <span className="tabular font-medium shrink-0 ml-2">{fmtMoney(session.estimated_due)}</span>
            </button>
          )}
        </div>
      )}
      {!isKiosk && cartOpen && (
        <div className="fixed inset-0 bg-[var(--sand-50)] z-50 overflow-y-auto">
          <div className="max-w-md mx-auto p-4 pb-32">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-2xl font-semibold">Your order</h3>
              <button onClick={() => setCartOpen(false)} data-testid="close-cart"><X size={20} /></button>
            </div>
            {cart}
          </div>
        </div>
      )}
      {modOpen && <ModifierModal modOpen={modOpen} setModOpen={setModOpen} onConfirm={confirmMods} />}
    </div>
  );
}

function BackToAi({ onClose, compact, touch }) {
  return (
    <button data-testid="ai-browse-back" onClick={onClose}
      className={`px-5 ${compact ? "h-14 shadow-lg" : "h-12"} rounded-full bg-[var(--terracotta)] text-white text-base font-medium inline-flex items-center gap-2 active:scale-95 transition-transform shrink-0`}>
      {touch ? <ArrowLeft size={18} weight="bold" /> : <Microphone size={18} weight="fill" />} {touch ? "Back to order" : compact ? "Back to AI" : "Back to AI cashier"}
    </button>
  );
}

// Same cart lines and totals as the No AI kiosk; checkout continues in the AI Cashier's own steps.
function AiCart({ lines, session, onInc, onDec, onRemove, onCheckout, onClose }) {
  const cr = session.credit || {};
  return (
    <>
      <CartLines lines={lines} onInc={onInc} onDec={onDec} onRemove={onRemove} />
      <CartTotals subtotal={session.cart_total} total={session.estimated_due}
        extra={cr.apply_amount > 0 ? <div className="flex justify-between" data-testid="ai-browse-credit"><span className="text-[var(--ink-500)]">Account credit</span><span className="tabular">− {fmtMoney(cr.apply_amount)}</span></div> : null} />
      <button data-testid="ai-browse-checkout" onClick={onCheckout} disabled={!lines.length}
        className="w-full h-12 mt-4 rounded-md bg-[var(--terracotta)] hover:bg-[var(--terracotta-dark)] text-white font-medium disabled:opacity-50 active:scale-[0.98]">
        Continue to checkout - {fmtMoney(session.estimated_due)}
      </button>
      <button data-testid="ai-browse-keep-ordering" onClick={onClose}
        className="w-full h-12 mt-2 rounded-md bg-[var(--ink-900)] hover:bg-black text-[var(--sand-50)] font-medium active:scale-[0.98]">
        {session.flow?.touch_only ? "Back to order" : "Back to AI cashier"}      </button>
    </>
  );
}
